import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import EditableField from "../components/EditableField";
import { deleteKitIfNoCases } from "../utils/kitHelpers";
import toast from "react-hot-toast";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";

export default function KitDetail() {
	const { id } = useParams();
	const { role, user } = useAuth();

	const [kitData, setKitData] = useState(null);
	const [associatedCases, setAssociatedCases] = useState([]);
	const [allCases, setAllCases] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showCaseModal, setShowCaseModal] = useState(false);

	// Fetch kit info and associated cases
	const fetchAssociatedCases = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			// Get kit info
			const { data: kit, error: kitError } = await supabase
				.from("kits")
				.select("*")
				.eq("id", id)
				.single();
			if (kitError) throw kitError;
			setKitData(kit);

			// Get associated cases
			const { data: kitCasesData, error: kitCasesError } = await supabase
				.from("kit_cases")
				.select(
					`
				cases (
					id,
					name,
					weight,
					notes
				)
			`
				)
				.eq("kit_id", id);
			if (kitCasesError) throw kitCasesError;

			const cases = kitCasesData.map((kc) => kc.cases || kc.case || {});

			// Get gear statuses for associated cases
			const caseIds = cases.map((c) => c.id);
			const { data: gearData, error: gearError } = await supabase
				.from("gear")
				.select("case_id, gear_statuses(label)")
				.in("case_id", caseIds);

			if (gearError) throw gearError;

			// Map gear issues by case
			const statusMap = {};
			gearData.forEach(({ case_id, gear_statuses }) => {
				if (!gear_statuses) return;
				if (!statusMap[case_id]) statusMap[case_id] = new Set();
				statusMap[case_id].add(gear_statuses.label);
			});

			const mappedCases = cases
				.map((c) => ({
					...c,
					statusFlags: statusMap[c.id] || new Set(),
				}))
				.sort((a, b) =>
					a.name.localeCompare(b.name, undefined, {
						sensitivity: "base",
					})
				);

			setAssociatedCases(mappedCases);
		} catch (err) {
			setError("Failed to load kit data");
			console.error(err);
		}
		setLoading(false);
	}, [id]);

	async function handleDownloadKitQrSheet() {
		try {
			// Use the cases you've already loaded for this kit
			if (!associatedCases || associatedCases.length === 0) {
				alert("No cases in this kit.");
				return;
			}

			// PDF setup: Letter (612x792 pt)
			const doc = new jsPDF({ unit: "pt", format: "letter" });
			const cardW = 252; // 3.5" * 72
			const cardH = 144; // 2.0" * 72

			// Grid: 2 x 5 (10 per page)
			const marginX = 36,
				marginY = 36,
				colGap = 24,
				rowGap = 18;
			const cols = 2,
				rows = 4;

			// Load template once
			const templateDataUrl = await imgToDataURL(TEMPLATE.url);

			// Map template pixels -> card points
			const scaleX = cardW / TEMPLATE.pxW;
			const scaleY = cardH / TEMPLATE.pxH;
			const qrSizePt = TEMPLATE.qr.size * scaleX;
			const qrXPt = TEMPLATE.qr.x * scaleX;
			const qrYPt = TEMPLATE.qr.y * scaleY;

			let col = 0,
				row = 0;

			for (let i = 0; i < associatedCases.length; i++) {
				const c = associatedCases[i];
				const caseUrl = `${BASE_URL}/cases/${c.id}`;

				// Build QR
				const qrDataUrl = await QRCode.toDataURL(caseUrl, {
					errorCorrectionLevel: "H",
					margin: 1,
					scale: 8,
					color: { dark: "#000000", light: "#FFFFFF" },
				});

				// Slot position
				const cardX = marginX + col * (cardW + colGap);
				const cardY = marginY + row * (cardH + rowGap);

				// Draw template then QR
				doc.addImage(
					templateDataUrl,
					"PNG",
					cardX,
					cardY,
					cardW,
					cardH,
					undefined,
					"FAST"
				);
				doc.addImage(
					qrDataUrl,
					"PNG",
					cardX + qrXPt,
					cardY + qrYPt,
					qrSizePt,
					qrSizePt,
					undefined,
					"FAST"
				);

				// Advance grid
				col++;
				if (col >= cols) {
					col = 0;
					row++;
					if (row >= rows && i < associatedCases.length - 1) {
						doc.addPage();
						row = 0;
					}
				}
			}

			doc.save(`kit-${id}-qr-sheet.pdf`);
		} catch (err) {
			console.error("Failed to generate kit QR sheet:", err);
			alert("Failed to generate kit QR sheet. See console for details.");
		}
	}

	// Fetch all cases for the dropdown
	async function fetchAllCases() {
		try {
			const { data, error } = await supabase
				.from("cases")
				.select("id, name, kit_cases(kits(id, name, color))");

			if (error) throw error;
			setAllCases(data);
		} catch (err) {
			console.error(err);
		}
	}

	useEffect(() => {
		if (id) {
			fetchAssociatedCases();
			fetchAllCases();
		}
	}, [id, fetchAssociatedCases]);

	const BASE_URL = "https://berrymg.com/gear";

	// Match your single-card template config
	const TEMPLATE = {
		url: "/gear/card-1050x600.png", // same-origin recommended
		pxW: 1050, // 3.5" * 300dpi
		pxH: 600, // 2.0" * 300dpi
		qr: { x: 615, y: 10, size: 390 }, // <- adjust to your template’s QR hole
	};

	function imgToDataURL(src) {
		return new Promise((resolve, reject) => {
			const i = new Image();
			i.crossOrigin = "anonymous";
			i.onload = () => {
				const c = document.createElement("canvas");
				c.width = i.width;
				c.height = i.height;
				c.getContext("2d").drawImage(i, 0, 0);
				resolve(c.toDataURL("image/png"));
			};
			i.onerror = reject;
			i.src = src;
		});
	}

	// Add case to kit
	async function addCaseToKit(caseId) {
		const { error } = await supabase
			.from("kit_cases")
			.insert([{ kit_id: id, case_id: caseId }]);
		const toastId = toast.loading("Adding case...");
		if (error) {
			toast.error("Failed to add case to kit: " + error, { id: toastId });
			console.error(error);
		} else {
			toast.success("Case added successfully.", { id: toastId });
			await fetchAssociatedCases();
		}
	}

	// Remove case from kit
	async function removeCaseFromKit(caseId) {
		const { error } = await supabase
			.from("kit_cases")
			.delete()
			.eq("kit_id", id)
			.eq("case_id", caseId);
		const toastId = toast.loading("Removing case...");
		if (error) {
			toast.error("Failed to remove case from kit: " + error, {
				id: toastId,
			});
			console.error(error);
		} else {
			toast.success("Case removed successfully.", { id: toastId });
			await fetchAssociatedCases();
		}
	}

	async function updateKitField(field, value) {
		const { error } = await supabase
			.from("kits")
			.update({ [field]: value })
			.eq("id", id);
		const toastId = toast.loading("Updating kit...");
		if (error) {
			toast.error("Failed to update kit: " + error, {
				id: toastId,
			});
			return;
		}
		toast.success("Case updated successfully.", { id: toastId });
		setKitData((prev) => ({ ...prev, [field]: value }));
	}

	async function handleDeleteKit() {
		if (!window.confirm("Are you sure you want to delete this kit?"))
			return;

		const toastId = toast.loading("Deleting kit...");
		const result = await deleteKitIfNoCases(id);

		if (!result.success) {
			toast.error("Failed to delete kit: " + result.message, {
				id: toastId,
			});
		} else {
			toast.success("Kit deleted successfully.", { id: toastId });
			window.location.href = "/gear/kits";
		}
	}

	if (loading) {
		return (
			<div className="loading">
				<div>
					<i className="pi pi-spin pi-spinner"></i>
				</div>
			</div>
		);
	}
	if (error) return <div style={{ color: "red" }}>{error}</div>;
	if (!kitData) return <div>Kit not found.</div>;

	return (
		<div className="main">
			<h1>
				{role === "admin" ? (
					<EditableField
						value={kitData.name}
						onSave={(val) => updateKitField("name", val)}
						type="text"
						className="kit-detail__name-edit"
					/>
				) : (
					kitData.name
				)}
			</h1>

			<div className="kit-detail__color">
				<strong>Color: </strong>
				{role === "admin" ? (
					<EditableField
						value={kitData.color}
						onSave={(val) => {
							const trimmed = val.trim();
							const normalized =
								trimmed === ""
									? null
									: trimmed.startsWith("#")
									? trimmed
									: "#" + trimmed;
							updateKitField("color", normalized);
						}}
						type="text"
					/>
				) : (
					""
				)}
				{kitData.color && (
					<span
						className="kit-detail__color-box"
						style={{ backgroundColor: kitData.color }}
					></span>
				)}
			</div>

			<div className="kit-detail__notes">
				<strong>Notes:</strong>{" "}
				{user ? (
					<EditableField
						value={kitData.notes || ""}
						onSave={(val) => updateKitField("notes", val)}
						type="textarea"
						className="kit-notes"
					/>
				) : (
					<p style={{ whiteSpace: "pre-wrap" }}>
						{kitData.notes || "..."}
					</p>
				)}
			</div>

			<div className="kit-detail__cases">
				<div className="kit-detail__cases-lead">
					<h3>
						Associated Cases{" "}
						<span className="kit-detail__case-count">
							({associatedCases.length})
						</span>
					</h3>
					{role === "admin" && (
						<div className="kit-detail__case-add">
							<button
								onClick={() => setShowCaseModal(true)}
								className="button green"
							>
								<i className="pi pi-plus"></i>
								Add a Case
							</button>
						</div>
					)}
				</div>
				{associatedCases.length === 0 && (
					<p>No cases associated with this kit.</p>
				)}
				{associatedCases.map((c) => (
					<div key={c.id} className="list-item">
						<div className="list-item__name-row">
							<Link
								to={`/cases/${c.id}`}
								className="list-item__name"
							>
								{c.name}
							</Link>

							{c.weight && (
								<span className="list-item__case-weight">
									({c.weight} lbs)
								</span>
							)}

							{c.statusFlags?.has("Needs Repair") && (
								<span className="list-item__icon-wrapper repair">
									<i
										className="pi pi-wrench"
										title="Needs repair"
									/>
								</span>
							)}
							{c.statusFlags.has("Missing") && (
								<span className="list-item__icon-wrapper missing">
									<i
										className="pi pi-exclamation-triangle"
										title="Missing gear"
									/>
								</span>
							)}
							{c.statusFlags.has("In Use") && (
								<span className="list-item__icon-wrapper inuse">
									<i className="pi pi-ban" title="In Use" />
								</span>
							)}
						</div>
						{role === "admin" && (
							<button
								style={{ marginLeft: 10 }}
								onClick={() => removeCaseFromKit(c.id)}
								className="list-item__remove"
							>
								Remove
								<i className="pi pi-times"></i>
							</button>
						)}
					</div>
				))}
			</div>

			{role === "admin" && (
				<div className="kit-detail__delete-kit-button-wrap">
					<button
						onClick={handleDownloadKitQrSheet}
						className="button"
					>
						<i className="pi pi-download"></i>
						Download Kit QR Cards (PDF)
					</button>
					<button className="button red" onClick={handleDeleteKit}>
						<i className="pi pi-trash"></i>
						Delete Kit
					</button>
				</div>
			)}

			{showCaseModal && (
				<div
					className="modal-overlay"
					onClick={() => setShowCaseModal(false)}
				>
					<div
						className="modal-content"
						onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
					>
						<h2>Select a Case to Add</h2>
						<ul className="modal__case-list">
							{allCases
								.filter(
									(c) =>
										!associatedCases.some(
											(ac) => ac.id === c.id
										)
								)
								.sort((a, b) =>
									a.name.localeCompare(b.name, undefined, {
										sensitivity: "base",
									})
								)
								.map((c) => {
									const otherKits =
										c.kit_cases
											?.map((kc) => kc.kits)
											.filter(
												(k) => k.id !== kitData.id
											) ?? [];

									return (
										<li
											key={c.id}
											className="list-item case-list__list-item"
											onClick={async () => {
												await addCaseToKit(c.id);
												setShowCaseModal(false);
											}}
										>
											<strong>{c.name}</strong>
											{otherKits.length > 0 && (
												<div className="pill-wrapper">
													{otherKits.map((k) => (
														<span
															key={k.id}
															className="pill"
														>
															<span
																className="color"
																style={{
																	display:
																		"inline-block",
																	width: "10px",
																	height: "10px",
																	borderRadius:
																		"50%",
																	backgroundColor:
																		k.color ||
																		"#ccc",
																	marginRight:
																		"4px",
																}}
															></span>
															{k.name}
														</span>
													))}
												</div>
											)}
										</li>
									);
								})}
						</ul>

						<div className="add-kit__close-wrapper">
							<button
								className="button"
								onClick={() => setShowCaseModal(false)}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
